import { COMMON_SSO_ACCESS_TOKEN } from "../constants/sso";
import { logPageActionToNewRelic } from "@repo/shared-utils/newRelic-log-utils";
import { getLoginState } from "@repo/shared-utils/insert-params";
import { request } from "../core/request";
import { API_RESPONSE_CODES, BFL_WHEELES_ERROR, OPERATION_IDS } from "./constants";
import type { ICVInsertRequest, ICVInsertApiResponse } from "../types/cvInsertTypes";

/**
 * Insert commercial vehicle loan application
 *
 * @param data - Commercial vehicle loan application request data
 * @returns Promise with CV insert response
 *
 * @example
 * ```typescript
 * const response = await cvInsert({
 *   mobileNo: "9876543210",
 *   journeyName: "NCV_FRESH",
 *   prodName: "NCV",
 *   loanAmt: "800000",
 *   tenureInMon: "60",
 *   roi: "12.5",
 *   emiAmt: "18000"
 * });
 * ```
 */
export const cvInsert = async (data: ICVInsertRequest): Promise<ICVInsertApiResponse> => {
  try {
    const response = await request<ICVInsertApiResponse>({
      operationId: OPERATION_IDS.cvInsert,
      requestData: data,
    });

    if (response.statusCode !== API_RESPONSE_CODES.STATUS_CODE_7001) {
      throw new Error(JSON.stringify(response));
    }

    return response;
  } catch (error) {
    logPageActionToNewRelic(BFL_WHEELES_ERROR, {
      component: "Commercial Vehicle Insert",
      pageUrl: typeof window !== "undefined" ? window.location.href : "",
      message: (error as Error).message,
      debugInfo: JSON.stringify({
        loginState: getLoginState(),
        hasAToken: typeof window !== "undefined" && !!localStorage.getItem(COMMON_SSO_ACCESS_TOKEN),
        hasMobile: !!data.mobileNo,
      }),
    });
    return {} as ICVInsertApiResponse;
  }
};
